//apps/web/src/components/visitors/CheckInButton.tsx
import React, { useState } from 'react';
import { Button, CircularProgress, Tooltip, Box, SxProps, Theme } from '@mui/material';
import { Login, Logout } from '@mui/icons-material';

interface CheckInButtonProps {
  visitorId: string;
  visitorName: string;
  isCheckedIn?: boolean;
  onCheckIn?: () => Promise<void> | void;
  onCheckOut?: () => Promise<void> | void;
  size?: 'small' | 'medium' | 'large';
  variant?: 'text' | 'outlined' | 'contained';
  showLabel?: boolean;
  sx?: SxProps<Theme>;
}

const CheckInButton: React.FC<CheckInButtonProps> = ({
  visitorId: _visitorId,
  visitorName,
  isCheckedIn = false,
  onCheckIn,
  onCheckOut,
  size = 'medium',
  variant = 'contained',
  showLabel = true,
  sx,
}) => {
  const [loading, setLoading] = useState(false);

  const handleCheckIn = async () => {
    if (!onCheckIn) return;
    setLoading(true);
    try {
      await onCheckIn();
    } catch (error) {
      console.error('Check-in failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!onCheckOut) return;
    setLoading(true);
    try {
      await onCheckOut();
    } catch (error) {
      console.error('Check-out failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    if (isCheckedIn) {
      handleCheckOut();
    } else {
      handleCheckIn();
    }
  };

  const buttonText = isCheckedIn ? (showLabel ? 'Выход' : '') : showLabel ? 'Вход' : '';

  const tooltipText = isCheckedIn
    ? `Зарегистрировать выход: ${visitorName}`
    : `Зарегистрировать вход: ${visitorName}`;

  const buttonColor = isCheckedIn ? 'secondary' : 'primary';
  const buttonIcon = isCheckedIn ? (
    <Logout fontSize={size === 'small' ? 'small' : size === 'large' ? 'large' : 'medium'} />
  ) : (
    <Login fontSize={size === 'small' ? 'small' : size === 'large' ? 'large' : 'medium'} />
  );

  const getSizeProps = () => {
    switch (size) {
      case 'small':
        return { minHeight: 32, fontSize: '0.75rem' };
      case 'large':
        return { minHeight: 48, fontSize: '1rem' };
      default:
        return { minHeight: 40, fontSize: '0.875rem' };
    }
  };

  return (
    <Tooltip title={tooltipText} arrow>
      <Box
        onClick={(e) => {
          e.stopPropagation();
          handleClick();
        }}
        sx={{ display: 'inline-block' }}
      >
        <Button
          variant={variant}
          color={buttonColor}
          size={size}
          disabled={loading || (!onCheckIn && !onCheckOut)}
          startIcon={showLabel && buttonIcon}
          sx={{
            minWidth: loading ? (showLabel ? 80 : 40) : undefined,
            px: showLabel ? undefined : 1,
            transition: 'all 0.2s ease',
            ...getSizeProps(),
            ...sx,
          }}
        >
          {loading ? (
            <CircularProgress
              size={size === 'small' ? 16 : size === 'large' ? 24 : 20}
              color="inherit"
            />
          ) : (
            <>
              {!showLabel && buttonIcon}
              {showLabel && buttonText}
            </>
          )}
        </Button>
      </Box>
    </Tooltip>
  );
};

export default CheckInButton;
